import { QueryClient } from '@tanstack/react-query';

/**
 * Maximum retry attempts for transient failures (network blips, 5xx).
 * Auth failures (401/403) are never retried.
 */
const MAX_RETRY_ATTEMPTS = 2;

/**
 * Type guard for errors that expose a numeric `status` field
 * (e.g., the `ApiError` class to be introduced by TASK-010).
 *
 * Avoids coupling to the concrete error type so this module can be
 * implemented and validated independently of TASK-010.
 */
const hasHttpStatus = (error: unknown): error is { status: number } => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  );
};

/**
 * Returns `true` when the error represents an authentication or
 * authorization failure that should never be retried automatically.
 */
const isAuthError = (error: unknown): boolean => {
  if (!hasHttpStatus(error)) return false;
  return error.status === 401 || error.status === 403;
};

/**
 * Shared TanStack Query client for the admin panel.
 *
 * Defaults follow DESIGN section 7.3:
 * - 30s `staleTime` keeps tab switches snappy without serving stale data
 *   beyond the operator's tolerance.
 * - 5min `gcTime` retains caches long enough to power back-navigation.
 * - `retry` skips 401/403 (auth flow handles those) and otherwise allows
 *   up to 2 attempts for transient failures.
 * - Refetch on window focus / reconnect keeps long-lived dashboards fresh.
 * - Mutations never auto-retry to avoid duplicate side effects.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        if (isAuthError(error)) return false;
        return failureCount < MAX_RETRY_ATTEMPTS;
      },
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false,
    },
  },
});
